import { StandardEvents } from '@shopify/events';

class CgWizardBarfCaneEngine extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.wizard = this.closest('cg-feeding-wizard');
    if (!this.wizard || this.wizard.dataset.calculatorType !== 'barf_cane') return;

    this.variants = this.parseVariants();
    this.syncVariant(this.dataset.selectedVariantId || this.wizard.dataset.selectedVariantId);
    this.wizard.addEventListener('cg:wizard:calculate', this.handleCalculate, { signal });
    document.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  parseVariants() {
    const script = this.wizard.querySelector('[data-wizard-variants]');
    if (!script?.textContent) return new Map();
    try {
      const variants = JSON.parse(script.textContent);
      return Array.isArray(variants)
        ? new Map(variants.map((variant) => [String(variant.id), variant]))
        : new Map();
    } catch (error) {
      console.error('[CG Wizard BARF Cane] Impossibile leggere le varianti.', error);
      return new Map();
    }
  }

  syncVariant(variantId) {
    this.variant = variantId == null || variantId === ''
      ? undefined
      : this.variants.get(String(variantId));
  }

  handleCalculate = (event) => {
    if (event.target !== this.wizard || event.detail?.calculatorType !== 'barf_cane') return;
    this.lastValues = event.detail.values;
    this.render(this.lastValues);
  };

  render(values) {
    const weight = this.toNumber(values?.barf_weight);
    const profile = this.wizard.querySelector(
      `[data-wizard-name="profile"][data-wizard-value="${CSS.escape(String(values?.profile || ''))}"]`
    );
    const percentage = this.toNumber(profile?.dataset.barfPercentage);
    if (weight < 0.5 || weight > 100 || !percentage) {
      this.wizard.showResult({ note: 'Dati BARF non disponibili per la selezione.' });
      return;
    }

    const doseGrams = Math.round(weight * percentage * 1000);
    const packageWeight = this.toNumber(this.variant?.package_weight_g);
    const priceCents = this.toNumber(this.variant?.price);
    let duration;
    let cost;
    let note;

    if (packageWeight && doseGrams) {
      const daysExact = packageWeight / doseGrams;
      if (Number.isFinite(daysExact) && daysExact > 0) {
        duration = `Il formato da ${this.formatPackageWeight(packageWeight)} dura circa ${Math.max(1, Math.round(daysExact))} giorni`;
        const dailyCost = priceCents > 0 ? (priceCents / 100) / daysExact : 0;
        if (Number.isFinite(dailyCost) && dailyCost > 0) {
          cost = `${this.formatCurrency(dailyCost)} al giorno`;
        }
      }
    } else {
      note = 'Formato selezionato senza peso confezione disponibile.';
    }

    this.wizard.showResult({
      dose: `${this.formatNumber(doseGrams)} g / giorno`,
      duration,
      cost,
      note,
    });
    this.wizard.dataset.resultStale = 'false';
  }

  handleProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    const immediateVariantId = event.detail?.variantId;
    if (immediateVariantId != null && this.variants.has(String(immediateVariantId))) {
      this.updateVariant(immediateVariantId);
    }

    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise.then(({ detail }) => {
      if (detail?.productId != null && String(detail.productId) !== String(this.dataset.productId)) return;
      const variantId = detail?.resource?.id ?? detail?.variant?.id ?? detail?.id;
      this.updateVariant(variantId);
    }).catch(() => {});
  };

  updateVariant(variantId) {
    this.syncVariant(variantId);
    if (variantId != null) {
      this.dataset.selectedVariantId = String(variantId);
      this.wizard.dataset.selectedVariantId = String(variantId);
    }
    const result = this.wizard.querySelector('[data-wizard-result]');
    if (this.lastValues && result && !result.hidden) this.render(this.lastValues);
  }

  toNumber(value) {
    if (value == null || value === '') return 0;
    const number = Number(typeof value === 'string' ? value.replace(',', '.').trim() : value);
    return Number.isFinite(number) ? number : 0;
  }

  formatNumber(value, maximumFractionDigits = 0) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits }).format(value);
    } catch {
      return String(value);
    }
  }

  formatPackageWeight(grams) {
    return grams >= 1000
      ? `${this.formatNumber(grams / 1000, 2)} kg`
      : `${this.formatNumber(grams)} g`;
  }

  formatCurrency(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      }).format(value);
    } catch {
      return `€${Number(value).toFixed(2)}`;
    }
  }
}

if (!customElements.get('cg-wizard-barf-cane-engine')) {
  customElements.define('cg-wizard-barf-cane-engine', CgWizardBarfCaneEngine);
}
